/**
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isString from 'lodash/isString';

import { neverThrow } from '../../util.type';
import { resolveVar } from '../variables/variables-util';
import { getConditionsEvaluationStrategy } from './conditions-evaluation-strategy';
import {
  AggregateEvaluationError,
  EvaluationError,
  NumberCoercionError,
  UnknownNodeError,
  VariableNotFoundError
} from './errors';

/**
 * @import {
 *  BooleanExpression,
 *  TrackConditions,
 *  StringExpression,
 *  BinaryShape,
 *  StringSetExpression,
 *  Node,
 *  LogicalBooleanExpression
 * } from '.'
 * @import { IVariablesNamespaceRegistry } from '../variables'
 */

/**
 * @param {string} value
 * @param {StringExpression} node
 * @returns {number}
 * @throws {NumberCoercionError}
 */
function coerceToNumber(value, node) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {
    throw new NumberCoercionError({ value, node });
  }
  return numberValue;
}

/**
 * @param {BinaryShape<'IN', StringExpression, StringSetExpression>} node
 * @param {IVariablesNamespaceRegistry} registry
 * @param {EvaluationError[]} [warnings]
 * @returns {boolean}
 */
function compareIn(node, registry, warnings) {
  const left = evaluateString(node.lhs, registry, warnings);
  const right = evaluateStringSet(node.rhs, registry, warnings);
  return right.has(left);
}

/**
 * @param {BinaryShape<'GTCN' | 'LTCN' | 'GECN' | 'LECN', StringExpression>} node
 * @param {IVariablesNamespaceRegistry} registry
 * @param {(left: number, right: number) => boolean} comparator
 * @param {EvaluationError[]} [warnings]
 * @returns {boolean}
 */
function compareNumeric(node, registry, comparator, warnings) {
  const lhs = evaluateString(node.lhs, registry, warnings);
  const rhs = evaluateString(node.rhs, registry, warnings);

  const l = coerceToNumber(lhs, node.lhs);
  const r = coerceToNumber(rhs, node.rhs);

  return comparator(l, r);
}

/**
 * @param {BinaryShape<'EQ' | 'NE' | 'CONTAINS', StringExpression>} node
 * @param {IVariablesNamespaceRegistry} registry
 * @param {(left: string, right: string) => boolean} comparator
 * @param {EvaluationError[]} [warnings]
 * @returns {boolean}
 */
function compareString(node, registry, comparator, warnings) {
  const lhs = evaluateString(node.lhs, registry, warnings);
  const rhs = evaluateString(node.rhs, registry, warnings);
  return comparator(lhs, rhs);
}

/**
 * @param {BinaryShape<'ALLOF' | 'ANYOF', StringSetExpression>} node
 * @param {IVariablesNamespaceRegistry} registry
 * @param {(left: Set<string>, right: Set<string>) => boolean} comparator
 * @param {EvaluationError[]} [warnings]
 * @returns {boolean}
 */
function compareStringSet(node, registry, comparator, warnings) {
  const left = evaluateStringSet(node.lhs, registry, warnings);
  const right = evaluateStringSet(node.rhs, registry, warnings);
  return comparator(left, right);
}

/**
 * @param {TrackConditions | undefined} conditions
 * @param {IVariablesNamespaceRegistry} registry
 * @returns {boolean}
 */
export function evalConditions(conditions, registry) {
  if (!conditions) {
    return true;
  }
  const strategy = getConditionsEvaluationStrategy(conditions);
  return strategy.evaluate(conditions, registry);
}

/**
 * @param {BooleanExpression} node
 * @param {IVariablesNamespaceRegistry} registry
 * @param {EvaluationError[]} [warnings]
 * @returns {boolean}
 */
export function evaluate(node, registry, warnings) {
  switch (node.type) {
    case 'TRUE':
      return true;
    case 'FALSE':
      return false;
    case 'AND':
      return evaluateAnd(node, registry, warnings);
    case 'OR':
      return evaluateOr(node, registry, warnings);
    case 'NOT':
      return !evaluate(node.arg, registry, warnings);
    case 'TRYB':
      return evaluateTry(
        node.arg,
        node.def,
        (n) => evaluate(n, registry, warnings),
        warnings
      );
    case 'EQ':
      return compareString(node, registry, (a, b) => a === b, warnings);
    case 'NE':
      return compareString(node, registry, (a, b) => a !== b, warnings);
    case 'CONTAINS':
      return compareString(node, registry, (a, b) => a.includes(b), warnings);
    case 'GTCN':
      return compareNumeric(node, registry, (a, b) => a > b, warnings);
    case 'LTCN':
      return compareNumeric(node, registry, (a, b) => a < b, warnings);
    case 'GECN':
      return compareNumeric(node, registry, (a, b) => a >= b, warnings);
    case 'LECN':
      return compareNumeric(node, registry, (a, b) => a <= b, warnings);
    case 'IN':
      return compareIn(node, registry, warnings);
    // TODO: Po povýšení na ES2025 použiť množinové operace
    // isSupersetOf, intersection
    case 'ALLOF':
      return compareStringSet(
        node,
        registry,
        (a, b) => [...b].every((item) => a.has(item)),
        warnings
      );
    case 'ANYOF':
      return compareStringSet(
        node,
        registry,
        (a, b) => [...b].some((item) => a.has(item)),
        warnings
      );
    case 'UNKNOWN':
      throw new UnknownNodeError(node.originalType);
    default:
      throw new EvaluationError('Unknown node type', { node });
  }
}

/**
 * @param {LogicalBooleanExpression & { type: 'AND' }} node
 * @param {IVariablesNamespaceRegistry} registry
 * @param {EvaluationError[]} [warnings]
 * @returns {boolean}
 */
function evaluateAnd(node, registry, warnings) {
  /** @type {EvaluationError[]} */
  const errors = [];
  const res = node.args.every((arg) => {
    try {
      return evaluate(arg, registry, errors.concat(warnings || []));
    } catch (e) {
      errors.push(toEvaluationError(e, arg));
      return true;
    }
  });
  if (errors.length > 0) {
    throw new AggregateEvaluationError(errors);
  }
  return res;
}

/**
 * @param {LogicalBooleanExpression & { type: 'OR' }} node
 * @param {IVariablesNamespaceRegistry} registry
 * @param {EvaluationError[]} [warnings]
 * @returns {boolean}
 */
function evaluateOr(node, registry, warnings) {
  /** @type {EvaluationError[]} */
  const errors = [];
  const res = node.args.some((arg) => {
    try {
      return evaluate(arg, registry, errors.concat(warnings || []));
    } catch (e) {
      errors.push(toEvaluationError(e, arg));
      return false;
    }
  });
  if (errors.length > 0) {
    throw new AggregateEvaluationError(errors);
  }
  return res;
}

/**
 * @param {StringExpression} expression
 * @param {IVariablesNamespaceRegistry} registry
 * @param {EvaluationError[]} [warnings]
 * @returns {string}
 * @throws {VariableNotFoundError}
 */
function evaluateString(expression, registry, warnings) {
  const { type } = expression;
  switch (type) {
    case 'VAR': {
      const value = resolveVar(expression.name, registry);
      if (value === undefined || !isString(value)) {
        throw new VariableNotFoundError({
          name: expression.name,
          node: expression
        });
      }
      return value;
    }
    case 'CONST':
      return expression.val;
    case 'TRY':
      return evaluateTry(
        expression.arg,
        expression.def,
        (n) => evaluateString(n, registry),
        warnings
      );
    case 'UNKNOWN':
      throw new UnknownNodeError(expression.originalType);
    default:
      neverThrow(type);
  }
}

/**
 * @param {StringSetExpression} expression
 * @param {IVariablesNamespaceRegistry} registry
 * @param {EvaluationError[]} [warnings]
 * @returns {Set<string>}
 * @throws {VariableNotFoundError}
 */
function evaluateStringSet(expression, registry, warnings) {
  const { type } = expression;
  switch (type) {
    case 'VARS': {
      const value = resolveVar(expression.name, registry);
      if (!Array.isArray(value) || !value.every(isString)) {
        throw new VariableNotFoundError({
          name: expression.name,
          node: expression
        });
      }
      return new Set(value);
    }
    case 'CONSTS':
      return new Set(expression.val);
    case 'TRYS':
      return evaluateTry(
        expression.arg,
        expression.def,
        (n) => evaluateStringSet(n, registry),
        warnings
      );
    case 'UNKNOWN':
      throw new UnknownNodeError(expression.originalType);
    default:
      neverThrow(type);
  }
}

/**
 * @template {Node} N
 * @template T
 * @param {N} arg
 * @param {N} def
 * @param {(node: N) => T} evalCb
 * @param {EvaluationError[]} [warnings]
 * @returns {T}
 */
function evaluateTry(arg, def, evalCb, warnings) {
  try {
    return evalCb(arg);
  } catch (e) {
    const error = toEvaluationError(e, arg);
    if (warnings !== undefined) warnings.push(error);
    return evalCb(def);
  }
}

/**
 * @param {unknown} e
 * @param {Node} [node]
 * @returns {EvaluationError}
 */
function toEvaluationError(e, node) {
  if (e instanceof EvaluationError) return e;
  const message = e instanceof Error ? e.message : String(e);
  return new EvaluationError(message, { node });
}
